import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Unified install banner:
 *
 * 1. Chrome/Edge/Android: uses beforeinstallprompt for one-click install
 *    → Shown as a bottom banner with "Install" button
 *
 * 2. iOS Safari: manual instructions (tap Share → Add to Home Screen)
 *    → Bottom-sheet with inline share icon
 *
 * 3. iOS Chrome/Firefox: redirect-to-Safari banner with copy URL
 *    → Can't install PWA from non-Safari iOS browsers
 *
 * 4. Already standalone (installed): hidden entirely
 *
 * Dismissed banners don't reappear for 7 days (localStorage).
 */
export default function InstallBanner({ canInstall, promptInstall }) {
  const { t } = useTranslation();
  const [bannerType, setBannerType] = useState(null); // 'chrome-prompt' | 'safari' | 'ios-chrome' | null
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    // Already installed
    const isStandalone =
      window.navigator.standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches;
    if (isStandalone) return;

    // Check 7-day dismiss
    const dismissedAt = localStorage.getItem('aif_install_dismissed_at');
    if (dismissedAt) {
      const daysSince = (Date.now() - parseInt(dismissedAt, 10)) / (1000 * 60 * 60 * 24);
      if (daysSince < 7) return;
    }

    // Chrome/Edge/Android: beforeinstallprompt available
    if (canInstall) {
      setBannerType('chrome-prompt');
      return;
    }

    // iOS detection
    const ua = navigator.userAgent;
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    if (!isIOS) return;

    const isCriOS = /CriOS/.test(ua);
    const isFxiOS = /FxiOS/.test(ua);
    const isSafari = /Safari/.test(ua) && !isCriOS && !isFxiOS && !/OPiOS/.test(ua);

    if (isCriOS || isFxiOS) {
      const timer = setTimeout(() => setBannerType('ios-chrome'), 2000);
      return () => clearTimeout(timer);
    } else if (isSafari) {
      const timer = setTimeout(() => setBannerType('safari'), 3000);
      return () => clearTimeout(timer);
    }
  }, [canInstall]);

  const dismiss = () => {
    setBannerType(null);
    localStorage.setItem('aif_install_dismissed_at', String(Date.now()));
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(window.location.origin);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const input = document.createElement('input');
      input.value = window.location.origin;
      document.body.appendChild(input);
      input.select();
      document.execCommand('copy');
      document.body.removeChild(input);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (!bannerType) return null;

  // Chrome/Edge/Android — one-click install via beforeinstallprompt
  if (bannerType === 'chrome-prompt') {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[var(--aif-z-sticky)] animate-slideUp">
        <div
          className="mx-3 mb-3 bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] rounded-2xl shadow-[var(--aif-shadow-lg)] overflow-hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-[var(--aif-color-primary)] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md">
                <span className="text-white font-bold text-xl">A</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[var(--aif-text-primary)]">
                  {t('pwa.installTitle')}
                </div>
                <p className="text-xs text-[var(--aif-text-muted)] mt-1 leading-relaxed">
                  {t('pwa.installHint')}
                </p>
              </div>
              <button
                onClick={dismiss}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)] flex-shrink-0 -mt-1 -mr-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <button
              onClick={async () => {
                const accepted = await promptInstall();
                if (accepted) dismiss();
              }}
              className="mt-3 w-full py-2.5 px-4 bg-[var(--aif-color-primary)] text-white rounded-xl text-sm font-medium active:scale-[0.98] transition hover:opacity-90"
            >
              {t('pwa.installButton')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // iOS Chrome/Firefox — redirect to Safari
  if (bannerType === 'ios-chrome') {
    return (
      <div className="fixed bottom-0 left-0 right-0 z-[var(--aif-z-sticky)] animate-slideUp">
        <div
          className="mx-3 mb-3 bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] rounded-2xl shadow-[var(--aif-shadow-lg)] overflow-hidden"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <div className="p-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 bg-[var(--aif-color-primary)] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md">
                <span className="text-white font-bold text-xl">A</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-[var(--aif-text-primary)]">
                  AI Farm
                </div>
                <p className="text-xs text-[var(--aif-text-muted)] mt-1 leading-relaxed">
                  {t('pwa.openInSafari')}
                </p>
              </div>
              <button
                onClick={dismiss}
                className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)] flex-shrink-0 -mt-1 -mr-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <button
              onClick={copyUrl}
              className="mt-3 w-full flex items-center justify-center gap-2 py-2.5 px-4 bg-[var(--aif-bg-surface)] border border-[var(--aif-border-default)] rounded-xl text-sm font-medium text-[var(--aif-text-primary)] active:scale-[0.98] transition"
            >
              {copied ? (
                <>
                  <svg className="w-4 h-4 text-[var(--aif-color-primary)]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span className="text-[var(--aif-color-primary)]">{t('pwa.copied')}</span>
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15.666 3.888A2.25 2.25 0 0013.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 01-.75.75H9.75a.75.75 0 01-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 01-2.25 2.25H6.75A2.25 2.25 0 014.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 011.927-.184" />
                  </svg>
                  <span>{t('pwa.copyUrl')}</span>
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // iOS Safari — install instructions
  return (
    <div className="fixed bottom-0 left-0 right-0 z-[var(--aif-z-sticky)] animate-slideUp">
      <div
        className="mx-3 mb-3 bg-[var(--aif-bg-elevated)] border border-[var(--aif-border-default)] rounded-2xl shadow-[var(--aif-shadow-lg)] overflow-hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="p-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 bg-[var(--aif-color-primary)] rounded-2xl flex items-center justify-center flex-shrink-0 shadow-md">
              <span className="text-white font-bold text-xl">A</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm text-[var(--aif-text-primary)]">
                {t('pwa.installTitle')}
              </div>
              <p className="text-xs text-[var(--aif-text-muted)] mt-1.5 leading-relaxed flex items-center flex-wrap gap-1">
                <span>{t('pwa.tapOn')}</span>
                <span className="inline-flex items-center justify-center w-5 h-5 bg-[var(--aif-color-info)] rounded flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 8.25H7.5a2.25 2.25 0 00-2.25 2.25v9a2.25 2.25 0 002.25 2.25h9a2.25 2.25 0 002.25-2.25v-9A2.25 2.25 0 0016.5 8.25H15M12 2.25v12M12 2.25l3 3M12 2.25l-3 3" />
                  </svg>
                </span>
                <span>{t('pwa.thenAddToHome')}</span>
              </p>
            </div>
            <button
              onClick={dismiss}
              className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-[var(--aif-bg-surface)] transition text-[var(--aif-text-muted)] flex-shrink-0 -mt-1 -mr-1"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>
        <div className="flex justify-center pb-2">
          <div className="w-8 h-1 rounded-full bg-[var(--aif-border-default)]" />
        </div>
      </div>
    </div>
  );
}
